/* eslint-disable new-cap */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2017 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

/* global __webpack_public_path__:true */
/* eslint no-unused-vars:off, camelcase: off */

import PreviewDropinWrapper from "app/PreviewDropinWrapper";
import SelectBootstrap from "select/SelectBootstrap";
import PanBootstrap from "pan/PanBootstrap";
import CopyTextBootstrap from "contextMenuVerbs/CopyTextBootstrap";
import CopyHyperlinkBootstrap from "contextMenuVerbs/CopyHyperlinkBootstrap";
import OpenHyperlinkBootstrap from "contextMenuVerbs/OpenHyperlinkBootstrap";
import DownloadBootstrap from "download/DownloadBootstrap";
import FullScreenBootstrap from "fullScreen/FullScreenBootstrap";
import LiquidModeBootstrap from "liquidMode/LiquidModeBootstrap";
import ReadAloudBootstrap from "readAloud/ReadAloudBootstrap";
import DigSigBootstrap from "digsig/DigSigBootstrap";
import OrganizeRotateBootstrap from "organize/OrganizeRotateBootstrap";
import OrganizeDeleteBootstrap from "organize/OrganizeDeleteBootstrap";
import OrganizeInsertBootstrap from "organize/OrganizeInsertBootstrap";
import OrganizeReorderBootstrap from "organize/OrganizeReorderBootstrap";
import DocumentPropertiesBootstrap from "documentProperties/DocumentPropertiesBootstrap";
import WhatsNewDarkPageTheme from "containers/navigationBar/WhatsNewDarkPageTheme";

const label = "Preview";
const name = "Preview";
const id = "preview";

// Loads main drop-in code.
function load(serverPath) {
  __webpack_public_path__ = serverPath;
  // We need to set the public path so that require loads the
  // next module from the correct server.  Otherwise it would
  // load from the application host
  return Promise.resolve(PreviewDropinWrapper);
}

let bookmarksBootstrapPromise = null;

function loadBookmarks(serverPath) {
  if (bookmarksBootstrapPromise) {
    return bookmarksBootstrapPromise;
  }

  __webpack_public_path__ = serverPath;
  bookmarksBootstrapPromise = import(/* webpackChunkName: "rhpDropin" */ "bookmarks/BookmarksBootstrap").then(
    dropin => new dropin.default()
  );

  return bookmarksBootstrapPromise;
}

let thumbnailsBootstrapPromise = null;

function loadThumbnails(serverPath) {
  if (thumbnailsBootstrapPromise) {
    return thumbnailsBootstrapPromise;
  }

  __webpack_public_path__ = serverPath;
  thumbnailsBootstrapPromise = import(/* webpackChunkName: "rhpDropin" */ "thumbnails/ThumbnailsBootstrap").then(
    dropin => new dropin.default()
  );

  return thumbnailsBootstrapPromise;
}

let attachmentsBootstrapPromise = null;

function loadAttachments(serverPath) {
  if (attachmentsBootstrapPromise) {
    return attachmentsBootstrapPromise;
  }

  __webpack_public_path__ = serverPath;
  attachmentsBootstrapPromise = import(/* webpackChunkName: "rhpDropin" */ "attachments/AttachmentsBootstrap").then(
    dropin => new dropin.default()
  );

  return attachmentsBootstrapPromise;
}

let lmSettingsBootstrapPromise = null;

function loadLiquidModeSettingsBootstrap(serverPath) {
  if (lmSettingsBootstrapPromise) {
    return lmSettingsBootstrapPromise;
  }

  __webpack_public_path__ = serverPath;
  lmSettingsBootstrapPromise = import(/* webpackChunkName: "rhpDropin" */ "liquidMode/LMSettingsBootstrap").then(
    dropin => new dropin.default()
  );

  return lmSettingsBootstrapPromise;
}

function loadKeyboardShortcutProvider(serverPath) {
  __webpack_public_path__ = serverPath;
  return import(/* webpackChunkName: "keyboardshortcutprovider" */ "shortcutManager/KeyboardShortcutsProvider").then(
    dropin => dropin.default
  );
}

function loadPreviewProvider(serverPath) {
  __webpack_public_path__ = serverPath;
  return import(/* webpackChunkName: "previewProvider" */ "providers/previewProvider/PreviewProvider").then(
    dropin => dropin.default
  );
}

const dropins = {
  preview: {
    id,
    label,
    name,
    load
  },
  bookmarks: {
    id: "bookmarks",
    label: "Bookmarks",
    name: "Bookmarks",
    load: loadBookmarks
  },
  "preview-select": new SelectBootstrap(),
  "preview-pan": new PanBootstrap(),
  thumbnails: {
    id: "thumbnails",
    label: "Thumbnails",
    name: "Thumbnails",
    load: loadThumbnails
  },
  attachments: {
    id: "attachments",
    label: "Attachments",
    name: "Attachments",
    load: loadAttachments
  },
  signatures: new DigSigBootstrap(),
  permissions: new DocumentPropertiesBootstrap(),
  "verb-copy-text": new CopyTextBootstrap(),
  "verb-copy-link": new CopyHyperlinkBootstrap(),
  "verb-open-link": new OpenHyperlinkBootstrap(),
  "download-file": new DownloadBootstrap(),
  "verb-full-screen": new FullScreenBootstrap(),
  "verb-organize-rotate": new OrganizeRotateBootstrap(),
  "verb-organize-delete": new OrganizeDeleteBootstrap(),
  "verb-organize-insert": new OrganizeInsertBootstrap(),
  "verb-organize-reorder": new OrganizeReorderBootstrap(),
  "whats-new-dark-page-theme": new WhatsNewDarkPageTheme(),
  "verb-liquid-mode": new LiquidModeBootstrap(),
  "verb-read-aloud": new ReadAloudBootstrap(),
  "lm-settings": {
    id: "lm-settings",
    label: "Liquid Mode Settings",
    name: "Liquid Mode Settings",
    load: loadLiquidModeSettingsBootstrap
  }
};

const providers = {
  "keyboard-shortcuts-provider": {
    id: "keyboard-shortcuts-provider",
    load: loadKeyboardShortcutProvider
  },
  "preview-provider": {
    id: "preview-provider",
    load: loadPreviewProvider
  }
};

export { dropins, providers };
